import { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import { api } from '@/api/growth/dashboard/api';
import {
    GrowthDashboardDaysReq,
    CropPhosphoricAcidItem,
} from '@/api/growth/dashboard/type';
import Tooltip from '@components/common/Tooltip';

interface GrowthPhosphoricAcidInfoCardProps {
    gfIdx: number;
}

const GrowthPhosphoricAcidInfoCard = ({
    gfIdx,
}: GrowthPhosphoricAcidInfoCardProps) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<ApexCharts | null>(null);
    const [loading, setLoading] = useState(true);
    const [phosphoricAcidData, setPhosphoricAcidData] =
        useState<CropPhosphoricAcidItem | null>(null);
    const [selectedDays, setSelectedDays] = useState<number>(14);

    // 인산 데이터 조회
    useEffect(() => {
        if (gfIdx) {
            fetchPhosphoricAcidInfo(gfIdx, selectedDays);
        }
    }, [gfIdx, selectedDays]);

    const fetchPhosphoricAcidInfo = async (gfIdx: number, days: number) => {
        try {
            setLoading(true);
            const req: GrowthDashboardDaysReq = { gfIdx, days };
            const response = await api.getCropPhosphoricAcidCardInfo(req);
            setPhosphoricAcidData(response.data || null);
        } catch (error) {
            console.error('인산 정보 조회 실패:', error);
            setPhosphoricAcidData(null);
        } finally {
            setLoading(false);
        }
    };

    // 차트 렌더링
    useEffect(() => {
        if (!chartRef.current || !phosphoricAcidData) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }
            return;
        }

        // 기존 차트 인스턴스 제거
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        const leafstem = phosphoricAcidData.avgPLeafstem || 0;
        const pYield = phosphoricAcidData.avgPYield || 0;
        const root = phosphoricAcidData.avgPRoot || 0;
        const absorption = phosphoricAcidData.avgPAbsorption || 0;

        const options = {
            series: [leafstem, pYield, root, absorption],
            labels: ['잎줄기', '수확물', '뿌리', '흡수량'],
            chart: {
                height: 176,
                type: 'donut',
                dropShadow: {
                    enabled: true,
                    enabledOnSeries: undefined,
                    top: 5,
                    left: 0,
                    blur: 3,
                    color: '#000',
                    opacity: 0.1,
                },
            },
            dataLabels: {
                enabled: false,
            },
            legend: {
                show: false,
            },
            stroke: {
                show: true,
                curve: 'smooth',
                lineCap: 'round',
                colors: '#fff',
                width: 0,
                dashArray: 0,
            },
            plotOptions: {
                pie: {
                    expandOnClick: false,
                    donut: {
                        size: '70%',
                        background: 'transparent',
                        labels: {
                            show: true,
                            name: {
                                show: true,
                                fontSize: '20px',
                                color: '#495057',
                                offsetY: -4,
                            },
                            value: {
                                show: true,
                                fontSize: '18px',
                                offsetY: 8,
                                formatter: function (val: string) {
                                    return parseFloat(val).toFixed(2);
                                },
                            },
                            total: {
                                show: true,
                                showAlways: true,
                                label: '흡수량',
                                fontSize: '22px',
                                fontWeight: 600,
                                color: '#495057',
                                formatter: function (data: any) {
                                    return data.globals.series[3].toFixed(2);
                                },
                            },
                        },
                    },
                },
            },
            tooltip: {
                shared: true,
                intersect: false,
                y: {
                    formatter: function (val: number) {
                        return val.toFixed(2);
                    },
                },
            },
            colors: [
                'rgba(var(--pink-rgb), 0.8)',
                'rgba(var(--info-rgb), 0.9)',
                'rgba(var(--orange-rgb), 0.85)',
                'rgba(var(--success-rgb), 0.7)',
            ],
        };

        chartInstance.current = new ApexCharts(chartRef.current, options);
        chartInstance.current.render();

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [phosphoricAcidData]);

    // 기간 선택 버튼 핸들러
    const handleDaysChange = (days: number) => {
        setSelectedDays(days);
    };

    return (
        <div className="custom-card card">
            <div className="card-header justify-content-between align-items-center d-sm-flex d-block">
                <div className="card-title mb-sm-0 mb-2">
                    작물양분-인산
                    <Tooltip
                        content="작물의 인산 분포와 흡수량을 보여줍니다. 잎줄기, 수확물, 뿌리에 분포된 인산량과 총 흡수량을 확인할 수 있습니다."
                        customClass="tooltip-dark"
                        placement="top"
                    >
                        <i className="ri-question-line" />
                    </Tooltip>
                </div>
                <div
                    aria-label="Basic example"
                    role="group"
                    className="flex-wrap btn-group"
                >
                    <button
                        type="button"
                        className={`btn-wave btn btn-sm ${selectedDays === 7 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(7)}
                    >
                        7일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn btn-sm ${selectedDays === 14 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(14)}
                    >
                        14일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn btn-sm ${selectedDays === 30 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(30)}
                    >
                        30일
                    </button>
                    <button
                        type="button"
                        className={`btn-wave btn btn-sm ${selectedDays === 60 ? 'btn-success' : 'btn-success-light'}`}
                        onClick={() => handleDaysChange(60)}
                    >
                        60일
                    </button>
                </div>
            </div>

            {loading ? (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ height: '320px' }}
                >
                    <div className="spinner-border text-success" role="status">
                        <span className="visually-hidden">로딩중...</span>
                    </div>
                </div>
            ) : !phosphoricAcidData ? (
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ height: '329px' }}
                >
                    <div className="text-center text-muted">
                        인산 데이터가 없습니다
                        <br />
                        (콩, 옥수수등의 작물에 토양데이터가 있는경우에만
                        노출됩니다.)
                    </div>
                </div>
            ) : (
                <>
                    <div ref={chartRef} className="mt-4"></div>

                    <div className="card-body">
                        <div className="row-cols-12 row">
                            <div className="p-0 col">
                                <div className="text-center">
                                    <div className="mb-2">
                                        <i className="ri-check-line p-2 fs-15 bg-success-transparent rounded-circle lh-1 align-middle"></i>
                                    </div>
                                    <div className="text-muted fs-12 mb-1 hrm-jobs-legend job boards d-inline-block">
                                        흡수량
                                    </div>
                                    <div>
                                        <span className="fs-15 fw-semibold">
                                            {phosphoricAcidData.avgPAbsorption?.toFixed(
                                                2,
                                            ) || '0.00'}{' '}
                                            kg/ha
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="p-0 col">
                                <div className="text-center">
                                    <div className="mb-2">
                                        <i className="ri-check-line p-2 fs-15 bg-orange-transparent rounded-circle lh-1 align-middle"></i>
                                    </div>
                                    <div className="text-muted fs-12 mb-1 hrm-jobs-legend referrals d-inline-block">
                                        뿌리
                                    </div>
                                    <div>
                                        <span className="fs-15 fw-semibold">
                                            {phosphoricAcidData.avgPRoot?.toFixed(
                                                2,
                                            ) || '0.00'}
                                            %
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="p-0 col">
                                <div className="text-center">
                                    <div className="mb-2">
                                        <i className="ri-check-line p-2 fs-15 bg-info-transparent rounded-circle lh-1 align-middle"></i>
                                    </div>
                                    <div className="text-muted fs-12 mb-1 hrm-jobs-legend media d-inline-block">
                                        수확물
                                    </div>
                                    <div>
                                        <span className="fs-15 fw-semibold">
                                            {phosphoricAcidData.avgPYield?.toFixed(
                                                2,
                                            ) || '0.00'}
                                            %
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="p-0 col">
                                <div className="text-center">
                                    <div className="mb-2">
                                        <i className="ri-check-line p-2 fs-15 bg-pink-transparent rounded-circle lh-1 align-middle"></i>
                                    </div>
                                    <div className="text-muted fs-12 mb-1 hrm-jobs-legend campus d-inline-block">
                                        잎줄기
                                    </div>
                                    <div>
                                        <span className="fs-15 fw-semibold">
                                            {phosphoricAcidData.avgPLeafstem?.toFixed(
                                                2,
                                            ) || '0.00'}
                                            %
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default GrowthPhosphoricAcidInfoCard;
